"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsCampaignManagement = exports.googleAdsCampaignManagementArgsSchema = exports.GoogleAdsCampaignAction = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// ---------- Create Campaign ----------
var googleAdsCreateCampaignInputSchema = zod_1.z
    .object({
    name: zod_1.z
        .string()
        .min(1)
        .max(255, 'Campaign name must be 255 characters or less')
        .describe('The name of the campaign to create (max 255 chars). Must be unique within the account.'),
    objective: zod_1.z
        .enum(['SEARCH', 'DISPLAY', 'SHOPPING', 'PERFORMANCE_MAX'])
        .describe('The campaign objective/type.'),
    targetLocation: zod_1.z
        .string()
        .describe('Target location for the campaign. e.g. "United States", "New York", etc.'),
    dailyBudget: zod_1.z
        .number()
        .positive()
        .describe('Daily budget for the campaign in account currency (e.g. USD).'),
    bidStrategy: zod_1.z
        .enum([
        'MANUAL_CPC',
        'MAXIMIZE_CONVERSIONS',
        'MAXIMIZE_CONVERSION_VALUE',
        'TARGET_CPA',
        'TARGET_ROAS',
    ])
        .describe('Bidding strategy for the campaign. CRITICAL: For Performance Max campaigns, use MAXIMIZE_CONVERSION_VALUE (recommended) or TARGET_ROAS. MAXIMIZE_CONVERSIONS and TARGET_CPA may fail with Performance Max due to budget compatibility issues. For Search/Display campaigns, use MANUAL_CPC, MAXIMIZE_CONVERSIONS, or TARGET_CPA.'),
    targetCpa: zod_1.z
        .number()
        .positive()
        .nullish()
        .describe('Target CPA in account currency (required if bidStrategy is TARGET_CPA).'),
    targetRoas: zod_1.z
        .number()
        .positive()
        .nullish()
        .describe('Target ROAS as a decimal (e.g., 4.0 for 400% ROAS, required if bidStrategy is TARGET_ROAS).'),
    keywords: zod_1.z.array(zod_1.z.string()).nullish().describe('Keywords to target (for SEARCH campaigns).'),
    adGroupName: zod_1.z
        .string()
        .max(255, 'Ad group name must be 255 characters or less')
        .nullish()
        .describe('Name for the initial ad group (max 255 chars). If not provided, will use campaign name + " Ad Group".'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Campaign start date in YYYY-MM-DD format. Leave empty/null for immediate start (recommended).'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('Campaign end date in YYYY-MM-DD format. Leave empty/null to run indefinitely (recommended).'),
})
    .describe('Create a new Google Ads campaign with specified settings.');
var googleAdsCreateCampaignArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsCreateCampaignInputSchema);
var googleAdsCreateCampaignResponseSchema = zod_1.z.object({
    success: zod_1.z.boolean(),
    campaignId: zod_1.z.string().describe('The ID of the created campaign'),
    name: zod_1.z.string().describe('The name of the created campaign'),
    status: zod_1.z.string().describe('The current status of the campaign'),
    dailyBudget: zod_1.z.number().describe('The daily budget of the campaign'),
    bidStrategy: zod_1.z.string().describe('The bidding strategy of the campaign'),
    adGroupId: zod_1.z.string().nullish().describe('The ID of the created ad group (if applicable)'),
    keywordsAdded: zod_1.z.number().nullish().describe('Number of keywords added to the campaign'),
    message: zod_1.z.string().nullish().describe('Additional information about the campaign creation'),
    alreadyExisted: zod_1.z.boolean().nullish().describe('Whether the campaign already existed'),
});
var googleAdsCreateCampaignResultSchema = googleAdsCreateCampaignResponseSchema;
// ---------- Update Campaign ----------
var googleAdsUpdateCampaignInputSchema = zod_1.z
    .object({
    campaignId: zod_1.z.string().min(1).describe('The ID of the campaign to update.'),
    name: zod_1.z.string().nullish().describe('New name for the campaign (optional).'),
    dailyBudget: zod_1.z
        .number()
        .positive()
        .nullish()
        .describe('New daily budget for the campaign in account currency (optional).'),
    bidStrategy: zod_1.z
        .enum([
        'MANUAL_CPC',
        'MAXIMIZE_CONVERSIONS',
        'MAXIMIZE_CONVERSION_VALUE',
        'TARGET_CPA',
        'TARGET_ROAS',
    ])
        .nullish()
        .describe('New bidding strategy for the campaign (optional). CRITICAL: For Performance Max campaigns, use MAXIMIZE_CONVERSION_VALUE (recommended) or TARGET_ROAS. MAXIMIZE_CONVERSIONS and TARGET_CPA may fail with Performance Max due to budget compatibility issues. For Search/Display campaigns, use MANUAL_CPC, MAXIMIZE_CONVERSIONS, or TARGET_CPA.'),
    targetCpa: zod_1.z
        .number()
        .positive()
        .nullish()
        .describe('Target CPA in account currency (optional, used with TARGET_CPA bidding strategy).'),
    targetRoas: zod_1.z
        .number()
        .positive()
        .nullish()
        .describe('Target ROAS as a decimal (optional, used with TARGET_ROAS bidding strategy).'),
    targetLocation: zod_1.z
        .string()
        .nullish()
        .describe('New target location for the campaign (optional).'),
    status: zod_1.z
        .enum(['ENABLED', 'PAUSED', 'REMOVED'])
        .nullish()
        .describe('New status for the campaign (optional).'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('New campaign start date in YYYY-MM-DD format (optional).'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('New campaign end date in YYYY-MM-DD format (optional).'),
})
    .describe('Update an existing Google Ads campaign with new settings.');
var googleAdsUpdateCampaignArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsUpdateCampaignInputSchema);
var googleAdsUpdateCampaignResponseSchema = zod_1.z.object({
    success: zod_1.z.boolean(),
    campaignId: zod_1.z.string().describe('The ID of the updated campaign'),
    updatedFields: zod_1.z.array(zod_1.z.string()).describe('List of fields that were updated'),
    name: zod_1.z.string().describe('The current name of the campaign'),
    status: zod_1.z.string().describe('The current status of the campaign'),
    dailyBudget: zod_1.z.number().describe('The current daily budget of the campaign'),
    bidStrategy: zod_1.z.string().describe('The current bidding strategy of the campaign'),
    targetCpa: zod_1.z
        .number()
        .nullish()
        .describe('Target CPA in account currency (when bidStrategy is TARGET_CPA)'),
    targetRoas: zod_1.z
        .number()
        .nullish()
        .describe('Target ROAS as a decimal (when bidStrategy is TARGET_ROAS)'),
    campaignType: zod_1.z
        .string()
        .nullish()
        .describe('The type of campaign (SEARCH, DISPLAY, PERFORMANCE_MAX, etc.)'),
    startDate: zod_1.z.string().nullish().describe('Campaign start date in YYYY-MM-DD format'),
    endDate: zod_1.z.string().nullish().describe('Campaign end date in YYYY-MM-DD format (if set)'),
    adGroupCount: zod_1.z
        .number()
        .nullish()
        .describe('Number of ad groups in the campaign (for non-Performance Max campaigns)'),
    assetGroupCount: zod_1.z
        .number()
        .nullish()
        .describe('Number of asset groups in the campaign (for Performance Max campaigns)'),
    message: zod_1.z.string().describe('Status message about the update operation'),
});
var googleAdsUpdateCampaignResultSchema = googleAdsUpdateCampaignResponseSchema;
// ==========================
// Compound Tool: googleAdsCampaignManagement
// ==========================
exports.GoogleAdsCampaignAction = zod_1.z.enum(['createCampaign', 'updateCampaign']);
exports.googleAdsCampaignManagementArgsSchema = zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({
            name: zod_1.z.literal('createCampaign'),
            params: googleAdsCreateCampaignArgsSchema.shape.args,
        }),
        zod_1.z.object({
            name: zod_1.z.literal('updateCampaign'),
            params: googleAdsUpdateCampaignArgsSchema.shape.args,
        }),
    ]),
    emitMode: zod_1.z.literal('single'),
});
var GoogleAdsCampaignManagementAllResultsSchema = zod_1.z.union([
    googleAdsCreateCampaignResultSchema,
    googleAdsUpdateCampaignResultSchema,
]);
var googleAdsCampaignManagementResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        { when: { 'action.name': 'createCampaign' }, schema: googleAdsCreateCampaignResultSchema },
        { when: { 'action.name': 'updateCampaign' }, schema: googleAdsUpdateCampaignResultSchema },
    ],
    defaultSchema: GoogleAdsCampaignManagementAllResultsSchema,
});
exports.googleAdsCampaignManagement = (0, common_1.createToolMetadata)({
    id: 'googleAdsCampaignManagement',
    displayName: 'Google Ads Campaign Management',
    displayDescription: 'Create and update Google Ads campaigns',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsCampaignManagementArgsSchema,
}, googleAdsCampaignManagementResultSchemas);
